import { HttpErrorResponse, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone, inject, makeStateKey, StateKey, TransferState } from '@angular/core';
import { Location } from '@angular/common';
import { catchError, EMPTY, Observable, Subject, tap, throwError } from 'rxjs';

import { environment } from '../../environments';
import { Page } from '../models';
import { IPageService, IQueryParams } from '@sirius/common';
import { UrlService } from './url.service';
import { LoadingSpinnerService } from 'shared';
import { IS_SERVER_SIDE, SSR_PAGE_DATA } from '../tokens';

@Injectable({
    providedIn: 'root',
})
export class PageDefaultService implements IPageService<Page> {
    private _httpClient = inject(HttpClient);
    private _transferState = inject(TransferState);
    private _urlService = inject(UrlService);
    private _location = inject(Location);
    private _isServerSide = inject(IS_SERVER_SIDE);
    private _ssrPageData = inject(SSR_PAGE_DATA);
    // `provideHttpClient(withFetch())` schedules the fetch and its
    // continuation via `NgZone.runOutsideAngular` — Angular's `FetchBackend`
    // never re-enters the Angular zone before calling `observer.next(...)`,
    // so any downstream RxJS operators and `pageChange` consumers run
    // outside the zone. With `provideZoneChangeDetection()` in `app.config`,
    // re-entering the zone via `_ngZone.run(...)` for our Subject emissions
    // and `loadingSpinner` state changes is what guarantees the default
    // change detection tick still fires for all subscribers.
    private _ngZone = inject(NgZone);

    pageChange = new Subject<Page>();
    errorMessage?: string;
    loadingSpinner = inject(LoadingSpinnerService);

    private _startPage = environment.startPage;
    private _fullUrl = '';
    private _baseUrl = '';

    private _hasFailed = new Subject<boolean>();
    hasFailed$ = this._hasFailed.asObservable();


    getPage(url: string, params: IQueryParams[] = []): Observable<Page> {
        this.loadingSpinner.show();
        const urlParts = this._urlService.segmentUrl(url);
        this._baseUrl =
            !urlParts.locationStr || urlParts.locationStr === '/'
                ? '/' + this._startPage
                : urlParts.locationStr;

        const stateKey: StateKey<Page> = makeStateKey<Page>('getPageData');
        const stateValue = this.getAndRemoveTransferState(stateKey);

        /*
        If a page state value exists reload the page with it and opt out of http request (SSR hydration).
        */
        if (stateValue) {
            this._ngZone.run(() => {
                this.pageChange.next(stateValue);
                this.loadingSpinner.hide();
            });
            return EMPTY;
        }

        if (urlParts.queryParamsStr) {
            const urlParams = this._urlService.toQueryParams(urlParts.queryParamsStr);

            /*
            If the URL parameter queryString is not also defined
            in params (function parameter value) - add them to it.
            */
            urlParams.forEach((x: IQueryParams) => {
                if (!params.flatMap((x: IQueryParams) => x.queryString).includes(x.queryString))
                    params.push(x);
            });
        }
        const stringParams = this._urlService.toStringParams(params);

        /*
        If SSR, we have pre-fetched page data injected - we use that instead of making an HTTP request.
        */
        if (this._isServerSide) {
            if (!this._ssrPageData) {
                throw new Error('PageDefaultService: _ssrPageData injection token is undefined');
            }
            try {
                const resp = JSON.parse(this._ssrPageData) as Page;
                this._transferState.set(stateKey, resp); // Store for client-side hydration
                this._handleResponse(resp, stateKey, stringParams, urlParts.fragmentStr);
                return EMPTY;
            } catch {
                throw new Error('PageDefaultService: Failed to parse _ssrPageData JSON');
            }
        }

        this._fullUrl = this._urlService.buildApiUrl(this._baseUrl, stringParams);
        this._hasFailed.next(false);

        /*
        Add custom header to inform API that we expect JSON response. Will replace "asjson" query parameter in future.
        We could alternatively use content negotiation (Accept header),
        but in prefer the explicit customer header to know the request is from our own frontend.
        */
        const httpOptions = { headers: new HttpHeaders({ 'TR-Response-Type': 'json' }) };

        return this._httpClient.get<Page>(this._fullUrl, httpOptions).pipe(
            catchError((err: HttpErrorResponse) =>
                this._ngZone.run(() => {
                    this._hasFailed.next(true);
                    this.loadingSpinner.hide();
                    return this._handleError(err);
                }),
            ),
            tap((resp: Page) => {
                this._handleResponse(resp, stateKey, stringParams, urlParts.fragmentStr);
            }),
        );
    }

    updatePage(params?: IQueryParams[]): Observable<Page> {
        return this.getPage(`${this._baseUrl}`, params);
    }

    private getAndRemoveTransferState(stateKey: StateKey<Page>): Page | null {
        const state = this._transferState.get(stateKey, null);

        if (state) {
            this._transferState.remove(stateKey);

            return state;
        }

        return null;
    }

    private _handleResponse(
        resp: Page,
        stateKey: StateKey<Page>,
        stringParams: string,
        fragmentStr?: string,
    ) {
        this._ngZone.run(() => {
            const status = resp.status;

            if (!status.success) {
                this.errorMessage = status.errorMessage || 'Unknown error';
                this._hasFailed.next(true);
            }

            /*
                Update URL with params when navigation event is not triggered,
                i.e. when updating the current page.
            */
            this._location.replaceState(
                this._baseUrl + stringParams + (fragmentStr ? `#${fragmentStr}` : ''),
                '',
                this._location.getState()
            );

            this.pageChange.next(resp);
            this.loadingSpinner.hide();
        });
    }

    private _handleError(error: HttpErrorResponse): Observable<never> {
        const msg = 'Something went wrong. Please try again later.';
        this.errorMessage = msg;

        return throwError(
            () =>
                new Error(
                    JSON.stringify(
                        {
                            status: error.status,
                            error: error.message,
                            url: error.url,
                        },
                        null,
                        4,
                    ),
                ),
        );
    }
}
